import React from 'react';

/**
 * Badge Component for status indicators
 */
const Badge = ({
  children,
  variant = 'default', // default, primary, success, warning, danger, info
  size = 'md',
  dot = false,
  className = '',
}) => {
  const baseStyles = 'inline-flex items-center font-medium rounded-full';

  const variants = {
    default: 'bg-[#F4F4F5] text-[#3F3F46] ring-1 ring-[#E4E4E7]',
    primary: 'bg-[#E0E7FF] text-[#3730A3] ring-1 ring-[#A5B4FC]/50',
    secondary: 'bg-[#EDE9FE] text-[#5B21B6] ring-1 ring-[#C4B5FD]/50',
    success: 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50',
    warning: 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50',
    danger: 'bg-[#FEE2E2] text-[#991B1B] ring-1 ring-[#FCA5A5]/50',
    info: 'bg-[#DBEAFE] text-[#1E40AF] ring-1 ring-[#93C5FD]/50',
    // Solid variants with clear edges
    'primary-solid': 'bg-[#4F46E5] text-white ring-1 ring-[#3730A3] shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
    'success-solid': 'bg-[#059669] text-white ring-1 ring-[#047857] shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
    'warning-solid': 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
    'danger-solid': 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
  };

  const sizes = {
    xs: 'px-1.5 py-0.5 text-[10px]',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1 text-sm',
  };

  const dotColors = {
    default: 'bg-[#71717A]',
    primary: 'bg-[#6366F1]',
    secondary: 'bg-[#8B5CF6]',
    success: 'bg-[#10B981]',
    warning: 'bg-[#F59E0B]',
    danger: 'bg-[#EF4444]',
    info: 'bg-[#3B82F6]',
  };

  return (
    <span
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotColors[variant] || dotColors.default}`} />
      )}
      {children}
    </span>
  );
};

export default Badge;

// Status Badge with predefined states
export const StatusBadge = ({ status, className = '' }) => {
  const statusConfig = {
    // Order statuses
    pending: { label: 'Pending', variant: 'warning', dot: true },
    requested: { label: 'Requested', variant: 'info', dot: true },
    picked: { label: 'Picked Up', variant: 'primary', dot: true },
    delivered: { label: 'Delivered', variant: 'success', dot: true },
    completed: { label: 'Completed', variant: 'success', dot: true },
    cancelled: { label: 'Cancelled', variant: 'danger', dot: true },
    
    // Priority statuses
    P0: { label: 'P0 Critical', variant: 'danger-solid' },
    P1: { label: 'P1 High', variant: 'warning-solid' },
    P2: { label: 'P2 Normal', variant: 'default' },
    
    // General statuses
    active: { label: 'Active', variant: 'success', dot: true },
    inactive: { label: 'Inactive', variant: 'default', dot: true },
    online: { label: 'Online', variant: 'success', dot: true },
    offline: { label: 'Offline', variant: 'default', dot: true },
    away: { label: 'Away', variant: 'warning', dot: true },
  };

  const config = statusConfig[status] || { label: status, variant: 'default' };

  return (
    <Badge variant={config.variant} dot={config.dot} className={className}>
      {config.label}
    </Badge>
  );
};

// Count Badge (for notifications, etc.)
export const CountBadge = ({
  count,
  max = 99,
  variant = 'danger-solid',
  className = '',
}) => {
  const displayCount = count > max ? `${max}+` : count;
  
  if (count <= 0) return null;

  return (
    <Badge variant={variant} size="xs" className={className}>
      {displayCount}
    </Badge>
  );
};

// Priority Badge with enhanced visibility
export const PriorityBadge = ({ priority, className = '' }) => {
  const config = {
    P0: { label: 'CRITICAL', variant: 'danger-solid', pulse: true },
    P1: { label: 'HIGH', variant: 'warning-solid', pulse: false },
    P2: { label: 'NORMAL', variant: 'default', pulse: false },
  };

  const { label, variant, pulse } = config[priority] || config.P2;

  return (
    <Badge 
      variant={variant} 
      size="sm" 
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
    >
      {label}
    </Badge>
  );
};

// Effort Badge with solid backgrounds and clear edges
export const EffortBadge = ({ effort, className = '' }) => {
  const config = {
    1: { color: 'bg-[#059669] text-white ring-1 ring-[#047857]' },
    2: { color: 'bg-[#0284C7] text-white ring-1 ring-[#0369A1]' },
    3: { color: 'bg-[#D97706] text-white ring-1 ring-[#B45309]' },
    4: { color: 'bg-[#EA580C] text-white ring-1 ring-[#C2410C]' },
    5: { color: 'bg-[#DC2626] text-white ring-1 ring-[#B91C1C]' },
  };

  const { color } = config[effort] || config[3];

  return (
    <span 
      className={`
        inline-flex items-center justify-center w-7 h-7 
        rounded-lg text-xs font-bold shadow-[0_2px_4px_rgba(0,0,0,0.1)]
        ${color} ${className}
      `}
    >
      {effort}
    </span>
  );
};

